/**
 * Mailbox-nudge analytics.
 *
 * One exported `track*` function per event. All fire as service
 * `control_panel` / entity `flock_order` via `sendOrderEvent`, which merges
 * `getCommonMedusaParams()` plus the order-only mailbox counts
 * (`getMailboxCountParams`: total_mailbox_license_count,
 * available_mailbox_license_count, total_mailbox_count). The two exceptions:
 *  - `trackMailAccountCreated` builds its payload through `createEventObj`,
 *    so common params are merged in manually.
 *  - `trackTabClicked` is the bundle event `b_tab_clicked`
 *    (`bundleEvents.logTabClicked`).
 */

import store from "@/store";
import { getDomainName } from "@/helpers";
import { sendOrderEvent } from "@/telemetry/medusaUtils";
import { getCommonMedusaParams, getMailboxCountParams } from "@/telemetry/medusaUtils";
import medusa from "@/telemetry/medusa";
import bundleEvents from "@/telemetry/bundleEvents";
import { MEDUSA_EVENTS_MAP } from "@/telemetry/events";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Where the Create-email screen was launched from (source_hook enum). */
export type TCreateEmailSourceHook =
  | "first_account_upfront_creation_screen"
  | "pending_account_page_main_button"
  | "email_listing_screen_button"
  | "create_email_account_banner"
  | "create_email_account_tab";

export type TCreationCancelledAction = "cancel" | "skip and go to control panel" | "breadcrumb";

export type TNudgeType = "generic mailbox nudge" | "pending mailbox nudge";

export type TMailboxAddType = "suggested" | "custom";

export type TNudgeContinuedAction = "Buy account" | "Create account" | "View all pending requests";

export type TPendingPageSourceHook =
  | "email accounts page pending banner"
  | "mailbox creation success";

export type TPendingMailboxAction = "Create account" | "Buy account" | "Remove account";

export type TCreateBannerType = "Create first account" | "Create more accounts";

export type TSuccessDismissedSourceHook = "breadcrumb" | "back to home" | "close";

export type TDownloadAppButtonName =
  | "mac_desktop_app"
  | "windows_desktop_app"
  | "play_store"
  | "app_store";

// ---------------------------------------------------------------------------
// Shared prop builders
// ---------------------------------------------------------------------------

/** Count of pending mailboxes — unique pending emails (grouped rows). */
export const getPendingMailboxCount = (): number =>
  store.getters["mail/mailboxNudge/pendingAccountGroups"]?.length ?? 0;

const isFirstMailbox = (): boolean =>
  (store.getters["currentDomainMailboxDetails"]?.length ?? 0) === 0;

const nudgeStateProps = () => ({
  pending_mailbox_count: getPendingMailboxCount(),
  is_first_mailbox: isFirstMailbox(),
});

/** Spread helper — include `key: value` only when value is meaningful. */
const ifPresent = (key: string, value: unknown): Record<string, unknown> =>
  value === undefined || value === null || value === "" ? {} : { [key]: value };

// ---------------------------------------------------------------------------
// Existing events — new properties
// ---------------------------------------------------------------------------

/**
 * `mail_account_created` with the spec's new props. Snapshot
 * `pendingMailboxCount`/`isFirstMailbox` BEFORE the created row is removed
 * from the pending list (the caller owns that ordering). Fires as `flock_order`
 * with the created mailbox as the `account_id` attribute; common params are
 * merged manually since `createEventObj` doesn't add them.
 */
export function trackMailAccountCreated(p: {
  email: string;
  type: string;
  sourceHook: TCreateEmailSourceHook;
  firstAccountAutoCreated: boolean;
  pendingMailboxCount: number;
  isFirstMailbox: boolean;
  /** Whether the CREATED mailbox is an admin mailbox (not the logged-in user). */
  isAdmin: boolean;
}): void {
  const attrs = {
    ...getCommonMedusaParams(),
    ...getMailboxCountParams(),
    eventName: MEDUSA_EVENTS_MAP.MAIL_ACCOUNT_CREATED,
    /* The created mailbox, as its own account id. Overrides the `account_id` that
       `getCommonMedusaParams` sets for an accountAdmin (the logged-in admin's id). */
    account_id: store.getters.accountIdByEmail(p.email),
    type: p.type,
    source_hook: p.sourceHook,
    first_account_auto_created: p.firstAccountAutoCreated,
    pending_mailbox_count: p.pendingMailboxCount,
    is_first_mailbox: p.isFirstMailbox,
    is_admin: p.isAdmin,
  };
  medusa.send(medusa.createEventObj(attrs, getDomainName(), "flock_order"));
}

export function trackDownloadNativeAppsButtonClick(p: {
  buttonName: TDownloadAppButtonName;
  sourceHook: string;
  viewSection?: string;
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.DOWNLOAD_NATIVE_APPS_PAGE_BUTTON_CLICK,
    button_name: p.buttonName,
    source_hook: p.sourceHook,
    ...ifPresent("view_section", p.viewSection),
  });
}

// ---------------------------------------------------------------------------
// New events — create email screen
// ---------------------------------------------------------------------------

export function trackNewEmailCreationViewed(p: {
  sourceHook: TCreateEmailSourceHook;
  prefilledUsername?: string;
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.NEW_EMAIL_CREATION_VIEWED,
    ...nudgeStateProps(),
    source_hook: p.sourceHook,
    ...ifPresent("prefilled_username", p.prefilledUsername),
  });
}

export function trackEmailCreationCancelled(p: {
  action: TCreationCancelledAction;
  sourceHook: TCreateEmailSourceHook;
  prefilledUsername?: string;
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.NEW_EMAIL_ACCOUNT_CREATION_CANCELLED,
    ...nudgeStateProps(),
    action: p.action,
    source_hook: p.sourceHook,
    ...ifPresent("prefilled_username", p.prefilledUsername),
  });
}

// ---------------------------------------------------------------------------
// New events — success screen
// ---------------------------------------------------------------------------

export function trackDownloadAppAccordionExpanded(p: {
  /** Same launcher source as new_email_creation_viewed — whatever opened the create screen. */
  sourceHook: TCreateEmailSourceHook;
  isFirstMailbox: boolean;
  /** Whether the just-created mailbox is an admin mailbox. */
  isAdmin: boolean;
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.DOWNLOAD_NATIVE_APP_ACCORDION_EXPANDED,
    pending_mailbox_count: getPendingMailboxCount(),
    is_first_mailbox: p.isFirstMailbox,
    source_hook: p.sourceHook,
    is_admin: p.isAdmin,
  });
}

/**
 * Success screen viewed. Caller supplies the same snapshots it
 * passes to `trackMailAccountCreated` (state at creation time, before the
 * pending row is removed).
 */
export function trackSuccessViewed(p: {
  sourceHook: TCreateEmailSourceHook;
  isFirstMailbox: boolean;
  /** Whether the CREATED mailbox is an admin mailbox. */
  isAdmin: boolean;
  pendingMailboxCount: number;
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.MAIL_ACCOUNT_CREATED_SUCCESS_VIEWED,
    pending_mailbox_count: p.pendingMailboxCount,
    is_first_mailbox: p.isFirstMailbox,
    source_hook: p.sourceHook,
    is_admin: p.isAdmin,
  });
}

export function trackSuccessDismissed(p: { sourceHook: TSuccessDismissedSourceHook }): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.MAIL_ACCOUNT_CREATED_SUCCESS_DISMISSED,
    pending_mailbox_count: getPendingMailboxCount(),
    source_hook: p.sourceHook,
  });
}

// ---------------------------------------------------------------------------
// New events — add-mailbox nudge (success-screen nudge)
// ---------------------------------------------------------------------------

export function trackNudgeShown(p: { nudgeType: TNudgeType; suggestedMailboxes: string[] }): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.ADD_MAILBOX_NUDGE_SHOWN,
    nudge_type: p.nudgeType,
    pending_mailbox_count: getPendingMailboxCount(),
    suggested_mailboxes: p.suggestedMailboxes,
  });
}

export function trackNudgeAddClicked(p: {
  nudgeType: TNudgeType;
  suggestedMailboxes: string[];
  addedMailboxUsername: string;
  addedMailboxType: TMailboxAddType;
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.ADD_MAILBOX_NUDGE_ADD_CLICKED,
    nudge_type: p.nudgeType,
    pending_mailbox_count: getPendingMailboxCount(),
    suggested_mailboxes: p.suggestedMailboxes,
    added_mailbox_username: p.addedMailboxUsername,
    added_mailbox_type: p.addedMailboxType,
  });
}

export function trackNudgeRemoveClicked(p: {
  nudgeType: TNudgeType;
  suggestedMailboxes: string[];
  removedMailboxUsername: string;
  removedMailboxType: TMailboxAddType;
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.ADD_MAILBOX_NUDGE_REMOVE_CLICKED,
    nudge_type: p.nudgeType,
    pending_mailbox_count: getPendingMailboxCount(),
    suggested_mailboxes: p.suggestedMailboxes,
    removed_mailbox_username: p.removedMailboxUsername,
    removed_mailbox_type: p.removedMailboxType,
  });
}

export function trackNudgeContinued(p: {
  nudgeType: TNudgeType;
  action: TNudgeContinuedAction;
  mailboxesAddedCount: number;
  mailboxesAddedUsername: string[];
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.ADD_MAILBOX_NUDGE_CONTINUED,
    nudge_type: p.nudgeType,
    pending_mailbox_count: getPendingMailboxCount(),
    mailboxes_added_count: p.mailboxesAddedCount,
    mailboxes_added_username: p.mailboxesAddedUsername,
    action: p.action,
  });
}

// ---------------------------------------------------------------------------
// New events — pending accounts banner & page
// ---------------------------------------------------------------------------

export function trackPendingBannerShown(): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.PENDING_EMAIL_ACCOUNT_BANNER_SHOWN,
    pending_mailbox_count: getPendingMailboxCount(),
  });
}

export function trackPendingBannerClicked(): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.PENDING_EMAIL_ACCOUNT_BANNER_CLICKED,
    pending_mailbox_count: getPendingMailboxCount(),
  });
}

export function trackPendingPageViewed(p: { sourceHook: TPendingPageSourceHook }): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.PENDING_EMAIL_ACCOUNT_PAGE_VIEWED,
    pending_mailbox_count: getPendingMailboxCount(),
    source_hook: p.sourceHook,
  });
}

export function trackPendingMailboxClicked(p: {
  action: TPendingMailboxAction;
  mailboxUsername: string;
  /**
   * Per requester of the clicked row, derived from the requester's email:
   * "admin" (an admin mailbox on the order), "non-admin" (a regular
   * mailbox), or "customer" (no mailbox match — the customer entity).
   */
  mailboxSuggestedBy: string[];
}): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.PENDING_MAILBOX_CLICKED,
    action: p.action,
    mailbox_username: p.mailboxUsername,
    mailbox_suggested_by: p.mailboxSuggestedBy,
    pending_mailbox_count: getPendingMailboxCount(),
  });
}

// ---------------------------------------------------------------------------
// New events — iframe homepage create banner
// ---------------------------------------------------------------------------

export function trackCreateEmailBannerShown(p: { bannerType: TCreateBannerType }): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.CREATE_EMAIL_ACCOUNT_BANNER_SHOWN,
    pending_mailbox_count: getPendingMailboxCount(),
    banner_type: p.bannerType,
  });
}

export function trackCreateEmailBannerClicked(p: { bannerType: TCreateBannerType }): void {
  sendOrderEvent({
    eventName: MEDUSA_EVENTS_MAP.CREATE_EMAIL_ACCOUNT_BANNER_CLICKED,
    pending_mailbox_count: getPendingMailboxCount(),
    banner_type: p.bannerType,
  });
}

// ---------------------------------------------------------------------------
// New events — bundle tab click
// ---------------------------------------------------------------------------

/**
 * `b_tab_clicked` — order ids are omitted entirely when absent.
 * `tabName` is the tab's stable analytics `id` (e.g. "email_accounts"),
 * defined alongside each nav item in SidebarNav/config.js and cpWidget's
 * routes — locale-independent by construction.
 */
export function trackTabClicked(p: { tabName: string }): void {
  bundleEvents.logTabClicked({
    tab_name: p.tabName,
    ...ifPresent("mail_order_id", Number(store.getters["mail/orderId"]) || undefined),
    ...ifPresent("site_order_id", Number(store.getters["site/orderId"]) || undefined),
  });
}
